import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db, functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';

const AdminSignInForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const updateLastLoginAt = async (userId: string) => {
    try {
      // 사용자 프로필 문서가 존재하는지 확인
      const userProfileRef = doc(db, 'user_profiles', userId);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        // lastLoginAt 필드 업데이트
        await updateDoc(userProfileRef, {
          lastLoginAt: new Date().toISOString()
        });
        console.log('관리자 lastLoginAt 업데이트 완료');
      } else {
        console.warn('관리자 프로필을 찾을 수 없습니다:', userId);
      }
    } catch (error) {
      console.error('관리자 lastLoginAt 업데이트 실패:', error);
      // lastLoginAt 업데이트 실패는 로그인 자체를 막지 않도록 함
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 로그인 시도
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // 사용자 권한 확인
      const userDoc = await getDoc(doc(db, 'user_profiles', userCredential.user.uid));
      const userData = userDoc.data();

      if (!userData || userData.userLevel > 2) {
        // 권한이 없는 경우 로그아웃
        await auth.signOut();
        throw new Error('관리자 권한이 없습니다.');
      }

      // 로그인 성공 시 lastLoginAt 업데이트
      await updateLastLoginAt(userCredential.user.uid);

      // 기존 관리자 계정도 로그인 시 Auth Custom Claims를 Firestore 권한과 동기화
      const syncAdminClaims = httpsCallable(functions, 'syncAdminClaims');
      await syncAdminClaims({ uid: userCredential.user.uid });
      await userCredential.user.getIdToken(true);

      // 관리자 대시보드로 이동
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : '로그인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="absolute left-[40px] md:left-[74px] top-[178px] md:top-[197px] w-[calc(100%-80px)] md:w-[540px]">
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
          <p className="text-red-700 text-sm font-['Pretendard']">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-3">
            이메일 주소
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-sm font-['Pretendard'] focus:outline-none focus:ring-2 focus:ring-[#155acb] focus:border-transparent"
            placeholder="이메일을 입력하세요"
          />
        </div>

        <div>
          <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-3">
            비밀번호
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-sm font-['Pretendard'] focus:outline-none focus:ring-2 focus:ring-[#155acb] focus:border-transparent"
            placeholder="비밀번호를 입력하세요"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors disabled:bg-[#155acb]/50 disabled:cursor-not-allowed"
        >
          {loading ? '로그인 중...' : '관리자 로그인'}
        </button>
      </form>
      </div>
  );
};

export default AdminSignInForm; 
